<template>
    <div>
        <v-container fluid>
            <!-- Page Header -->
            <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
                <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
                    <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{
                        "Detailed View"
                        }}</v-toolbar-title>
                    <v-spacer />
                    <div class="d-flex pr-4">
                        <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today
                            }}</v-toolbar-title>
                    </div>
                </v-toolbar>
            </v-skeleton-loader>
            <div class="d-flex align-center justify-end mt-4 mb-4">
                <div class="d-flex ga-2 align-center">
                    <!-- Run Selector Dropdown -->
                    <v-select v-model="selectedRunId" :items="selectedJobRuns" item-title="text" item-value="value"
                        variant="outlined" density="compact" hide-details style="min-width: 300px" :loading="loading">
                        <template #prepend-inner>
                            <v-icon size="small" color="primary">mdi-history</v-icon>
                        </template>
                    </v-select>
                    <v-btn color="primary" variant="elevated" @click="exportDialog = true" :disabled="!selectedRunData">
                        <v-icon class="mr-1">mdi-download</v-icon>
                        Export
                    </v-btn>
                </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading && !selectedRunData" class="d-flex align-center justify-center" style="height: 400px;">
                <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            </div>

            <!-- Content -->
            <div v-else-if="selectedRunData">
                <!-- Intent Summary Cards - Show when validationType includes intent -->
                <v-row class="mb-4" v-if="hasIntentValidation">
                    <!-- Intent Accuracy Card -->
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" :class="getAccuracyClass(selectedRunData.intentAccuracy)">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ selectedRunData.intentAccuracy?.toFixed(1) || 0 }}%</div>
                                <v-tooltip location="bottom">
                                    <template #activator="{ props }">
                                        <div v-bind="props" class="text-body-2 mt-2 cursor-pointer">Intent Accuracy
                                            <v-icon size="x-small">mdi-help-circle-outline</v-icon></div>
                                    </template>
                                    <span>Did the bot understand what the customer is asking about?<br />This measures
                                        if the bot correctly identified ALL 4 levels<br />of the intent path.</span>
                                </v-tooltip>
                                <div class="text-caption text-grey mt-1">
                                    {{ selectedRunData.correctCount || 0 }}/{{ selectedRunData.totalQuestions }} full path match
                                </div>
                                <div class="d-flex justify-center ga-1 mt-2 flex-wrap">
                                    <v-tooltip location="bottom">
                                        <template #activator="{ props }">
                                            <v-chip v-bind="props" size="x-small" variant="tonal" color="grey">L1 {{ selectedRunData.l1Accuracy?.toFixed(0) || 0 }}%</v-chip>
                                        </template>
                                        <span>L1 = Brand/Channel match</span>
                                    </v-tooltip>
                                    <v-tooltip location="bottom">
                                        <template #activator="{ props }">
                                            <v-chip v-bind="props" size="x-small" variant="tonal" color="secondary">L2 {{ selectedRunData.l2Accuracy?.toFixed(0) || 0 }}%</v-chip>
                                        </template>
                                        <span>L2 = Category-level match</span>
                                    </v-tooltip>
                                    <v-tooltip location="bottom">
                                        <template #activator="{ props }">
                                            <v-chip v-bind="props" size="x-small" variant="tonal" color="info">L3 {{ selectedRunData.l3Accuracy?.toFixed(0) || 0 }}%</v-chip>
                                        </template>
                                        <span>L3 = Topic-level match</span>
                                    </v-tooltip>
                                    <v-tooltip location="bottom">
                                        <template #activator="{ props }">
                                            <v-chip v-bind="props" size="x-small" variant="tonal" color="primary">L4 {{ selectedRunData.l4Accuracy?.toFixed(0) || 0 }}%</v-chip>
                                        </template>
                                        <span>L4 = Most specific intent match</span>
                                    </v-tooltip>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <!-- TTFC Card (Intent) -->
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" variant="outlined">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold text-info">{{ formatTTFC(selectedRunData.avgTTFC) }}</div>
                                <v-tooltip location="bottom">
                                    <template #activator="{ props }">
                                        <div v-bind="props" class="text-body-2 mt-2 cursor-pointer">Avg TTFC <v-icon size="x-small">mdi-help-circle-outline</v-icon></div>
                                    </template>
                                    <span>Time To First Character - How fast does the bot start responding?</span>
                                </v-tooltip>
                                <div class="text-caption text-grey mt-1">Time to first character</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <!-- Fallback Rate Card -->
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" :class="getFallbackClass(selectedRunData.fallbackRate)">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ selectedRunData.fallbackRate?.toFixed(1) || 0 }}%</div>
                                <v-tooltip location="bottom">
                                    <template #activator="{ props }">
                                        <div v-bind="props" class="text-body-2 mt-2 cursor-pointer">Fallback Rate
                                            <v-icon size="x-small">mdi-help-circle-outline</v-icon></div>
                                    </template>
                                    <span>How often does the bot fail to understand?<br />Lower is better.</span>
                                </v-tooltip>
                                <div class="text-caption text-grey mt-1">{{ selectedRunData.fallbackCount || 0 }} fallback responses</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <!-- Questions Summary Card -->
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" variant="outlined">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold text-grey-darken-2">{{ selectedRunData.totalQuestions }}</div>
                                <div class="text-body-2 mt-2">Questions Tested</div>
                                <div class="text-caption text-grey mt-1">
                                    {{ selectedRunData.totalApiCalls }} API calls (R={{ selectedRunData.reliabilityCount }})
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Answer Summary Cards - Show when validationType includes answer -->
                <v-row class="mb-4" v-if="hasAnswerValidation">
                    <!-- Answer Quality Card -->
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" :class="getAnswerQualityClass(selectedRunData.answerQuality)">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ selectedRunData.answerQuality }}%</div>
                                <v-tooltip location="bottom">
                                    <template #activator="{ props }">
                                        <div v-bind="props" class="text-body-2 mt-2 cursor-pointer">Answer Quality
                                            <v-icon size="x-small">mdi-help-circle-outline</v-icon></div>
                                    </template>
                                    <span>Is the bot giving CORRECT answers?<br />ChatGPT evaluates if the bot's
                                        response
                                        matches<br />what we expect it to say (checked against guidelines or exact
                                        text).</span>
                                </v-tooltip>
                                <div class="text-caption text-grey mt-1">
                                    {{ selectedRunData.answerPassCount }}/{{ selectedRunData.answerEvalCount }}
                                    passed
                                </div>
                                <div class="d-flex justify-center ga-2 mt-2">
                                    <v-tooltip location="bottom">
                                        <template #activator="{ props }">
                                            <v-chip v-bind="props" size="x-small" variant="tonal" color="teal">
                                                g: {{ selectedRunData.guidelinePassCount }}/{{
                                                    selectedRunData.guidelineTotalCount
                                                }}
                                            </v-chip>
                                        </template>
                                        <span>Guideline Check: ChatGPT checks if the bot's answer<br />follows the
                                            expected
                                            guidelines/rules.<br />More flexible - allows different wording.</span>
                                    </v-tooltip>
                                    <v-tooltip location="bottom">
                                        <template #activator="{ props }">
                                            <v-chip v-bind="props" size="x-small" variant="tonal" color="deep-purple">
                                                e: {{ selectedRunData.exactPassCount }}/{{
                                                    selectedRunData.exactTotalCount }}
                                            </v-chip>
                                        </template>
                                        <span>Exact Check: ChatGPT checks if the bot's answer<br />matches the expected
                                            answer
                                            exactly.<br />Stricter - requires close word matching.</span>
                                    </v-tooltip>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <!-- Guideline Pass Rate Card -->
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" :class="getAnswerQualityClass(guidelinePassRate)">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ guidelinePassRate }}%</div>
                                <v-tooltip location="bottom">
                                    <template #activator="{ props }">
                                        <div v-bind="props" class="text-body-2 mt-2 cursor-pointer">Guideline Pass Rate
                                            <v-icon size="x-small">mdi-help-circle-outline</v-icon></div>
                                    </template>
                                    <span>Answers that follow business rules/guidelines.<br />ChatGPT evaluates if the
                                        bot's response<br />adheres to the expected guidelines.</span>
                                </v-tooltip>
                                <div class="text-caption text-grey mt-1">
                                    {{ selectedRunData.guidelinePassCount }}/{{ selectedRunData.guidelineTotalCount }}
                                    passed
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <!-- Exact Match Rate Card -->
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" :class="getAnswerQualityClass(exactPassRate)">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ exactPassRate }}%</div>
                                <v-tooltip location="bottom">
                                    <template #activator="{ props }">
                                        <div v-bind="props" class="text-body-2 mt-2 cursor-pointer">Exact Match Rate
                                            <v-icon size="x-small">mdi-help-circle-outline</v-icon></div>
                                    </template>
                                    <span>Answers that exactly match expected responses.<br />ChatGPT evaluates if the
                                        bot's response<br />closely matches the expected answer word-for-word.</span>
                                </v-tooltip>
                                <div class="text-caption text-grey mt-1">
                                    {{ selectedRunData.exactPassCount }}/{{ selectedRunData.exactTotalCount }}
                                    passed
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <!-- TTFC Card -->
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" variant="outlined">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold text-info">{{
                                    formatTTFC(selectedRunData.avgTTFC) }}
                                </div>
                                <v-tooltip location="bottom">
                                    <template #activator="{ props }">
                                        <div v-bind="props" class="text-body-2 mt-2 cursor-pointer">Avg TTFC <v-icon
                                                size="x-small">mdi-help-circle-outline</v-icon></div>
                                    </template>
                                    <span>Time To First Character - How fast does the bot start responding?<br />Lower
                                        is better.
                                        This is the time from when customer<br />sends a message until the bot starts
                                        typing.</span>
                                </v-tooltip>
                                <div class="text-caption text-grey mt-1">Time to first character</div>
                                <div v-if="selectedRunData.avgResponseTime" class="text-caption text-grey mt-1">
                                    Total: {{ formatMs(selectedRunData.avgResponseTime) }}
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Answer Quality Config & Response Time Row -->
                <v-row class="mb-4">
                    <v-col cols="12" md="6">
                        <v-card variant="outlined" class="h-100">
                            <v-card-title class="text-subtitle-1 pa-3 bg-grey-lighten-4">
                                <v-icon class="mr-2" size="small">mdi-timer-outline</v-icon>
                                Response Time
                            </v-card-title>
                            <v-card-text class="pa-3">
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <span class="text-body-2">Minimum:</span>
                                    <span class="font-weight-medium">{{ formatMs(selectedRunData.ttfcMin)
                                    }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <span class="text-body-2">Maximum:</span>
                                    <span class="font-weight-medium">{{ formatMs(selectedRunData.ttfcMax)
                                    }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center">
                                    <span class="text-body-2">Median:</span>
                                    <span class="font-weight-medium">{{ formatMs(selectedRunData.ttfcMedian)
                                    }}</span>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-card variant="outlined" class="h-100">
                            <v-card-title class="text-subtitle-1 pa-3 bg-grey-lighten-4">
                                <v-icon class="mr-2" size="small">mdi-cog-outline</v-icon>
                                Test Configuration
                            </v-card-title>
                            <v-card-text class="pa-3">
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <span class="text-body-2">Model ID:</span>
                                    <span class="font-weight-medium">{{ selectedRunData.modelId || '-' }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <span class="text-body-2">Judge Model ID:</span>
                                    <span class="font-weight-medium">{{ selectedRunData.judgeModelId || '-' }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <span class="text-body-2">Total Questions:</span>
                                    <span class="font-weight-medium">{{ selectedRunData.totalQuestions }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center">
                                    <span class="text-body-2">Total API Calls:</span>
                                    <span class="font-weight-medium">{{ selectedRunData.totalApiCalls }}</span>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Intent Summary by Category - Show when validationType includes intent -->
                <v-card class="mb-4" variant="outlined" v-if="hasIntentValidation">
                    <v-card-title class="text-subtitle-1 pa-3 bg-grey-lighten-4 d-flex align-center">
                        <v-icon class="mr-2" size="small">mdi-chart-bar</v-icon>
                        Accuracy by Category (Intent Summary)
                    </v-card-title>
                    <v-data-table 
                        :headers="intentCategoryHeaders" 
                        :items="intentCategoryBreakdownWithAverage"
                        :items-per-page="-1" 
                        density="compact" 
                        class="category-table"
                        :hide-default-footer="true">
                        <!-- Item Slots -->
                        <template #item.category="{ item }">
                            <div class="d-flex align-center" :class="{ 'font-weight-bold': item.isAverage }">
                                <v-icon v-if="!item.isAverage" size="small" color="primary" class="mr-2">mdi-folder-outline</v-icon>
                                <v-icon v-else size="small" color="grey" class="mr-2">mdi-sigma</v-icon>
                                {{ item.category }}
                            </div>
                        </template>
                        <template #item.accuracy="{ item }">
                            <v-chip :color="getScoreColor(item.accuracy)" size="x-small" variant="tonal" class="font-weight-medium">
                                {{ item.accuracy?.toFixed(1) }}%
                            </v-chip>
                        </template>
                        <template #item.accuracyL4="{ item }">
                            <span :class="getScoreTextColor(item.accuracyL4)">{{ item.accuracyL4?.toFixed(1) || '0.0' }}%</span>
                        </template>
                        <template #item.accuracyL3="{ item }">
                            <span :class="getScoreTextColor(item.accuracyL3)">{{ item.accuracyL3?.toFixed(1) || '0.0' }}%</span>
                        </template>
                        <template #item.accuracyL2="{ item }">
                            <span :class="getScoreTextColor(item.accuracyL2)">{{ item.accuracyL2?.toFixed(1) || '0.0' }}%</span>
                        </template>
                        <template #item.ttfc="{ item }">
                            {{ formatMs(item.ttfc) }}
                        </template>
                        <template #item.totalTime="{ item }">
                            {{ formatMs(item.totalTime) }}
                        </template>
                        <template #item.fallback="{ item }">
                            <span :class="item.fallback > 10 ? 'text-error' : item.fallback > 5 ? 'text-warning' : 'text-success'">
                                {{ item.fallback?.toFixed(1) || '0.0' }}%
                            </span>
                        </template>
                        <template #item.cVal="{ item }">
                            <span :class="item.cVal > 0 ? 'text-warning' : 'text-success'">{{ item.cVal?.toFixed(1) || '0.0' }}%</span>
                        </template>
                        <template #item.sVal="{ item }">
                            <span :class="item.sVal > 0 ? 'text-warning' : 'text-success'">{{ item.sVal?.toFixed(1) || '0.0' }}%</span>
                        </template>
                    </v-data-table>
                    <div class="px-4 py-2 bg-grey-lighten-5 text-caption">
                        <strong>Remarks:</strong>
                        <ul class="mt-1 mb-0" style="padding-left: 16px;">
                            <li><strong>Acc%:</strong> Original accuracy - requires same # of levels AND all levels match</li>
                            <li><strong>Acc(L4+)%:</strong> Match all levels that exist in expected (flexible)</li>
                            <li><strong>Acc(L3+)%:</strong> Match L1-L3 levels (first 3 levels)</li>
                            <li><strong>Acc(L2+)%:</strong> Match L1-L2 levels (first 2 levels)</li>
                            <li><strong>TTFC(ms):</strong> Time to First Chunk - time until the first response token is received (streaming latency)</li>
                            <li><strong>Total(ms):</strong> Total response time - complete round-trip time from request to full response</li>
                            <li><strong>Fallback%:</strong> Percentage of responses that triggered fallback/default handling</li>
                            <li><strong>cVal%:</strong> Category variance % (lower is better)</li>
                            <li><strong>sVal%:</strong> Score variance % (lower is better)</li>
                        </ul>
                    </div>
                </v-card>

                <!-- Answer Quality Summary by Category - Show when validationType includes answer -->
                <v-card class="mb-4" variant="outlined" v-if="hasAnswerValidation">
                    <v-card-title class="text-subtitle-1 pa-3 bg-grey-lighten-4 d-flex align-center">
                        <v-icon class="mr-2" size="small">mdi-message-check-outline</v-icon>
                        Accuracy by Category (Answer Quality Summary)
                        <v-spacer></v-spacer>
                        <v-chip size="small" color="success" variant="tonal">
                            LLM-as-Judge
                        </v-chip>
                    </v-card-title>
                    <v-data-table 
                        :headers="answerQualityCategoryHeaders" 
                        :items="answerQualityCategoryBreakdownWithAverage"
                        :items-per-page="-1" 
                        density="compact" 
                        class="category-table"
                        :hide-default-footer="true">
                        <!-- Item Slots -->
                        <template #item.category="{ item }">
                            <div class="d-flex align-center" :class="{ 'font-weight-bold': item.isAverage }">
                                <v-icon v-if="!item.isAverage" size="small" color="success" class="mr-2">mdi-folder-outline</v-icon>
                                <v-icon v-else size="small" color="grey" class="mr-2">mdi-sigma</v-icon>
                                {{ item.category }}
                            </div>
                        </template>
                        <template #item.answerAccuracy="{ item }">
                            <v-chip :color="getScoreColor(item.answerAccuracy)" size="x-small" variant="tonal" class="font-weight-medium">
                                {{ item.answerAccuracy?.toFixed(1) || '0.0' }}%
                            </v-chip>
                        </template>
                        <template #item.answerVariance="{ item }">
                            <span :class="item.answerVariance > 10 ? 'text-error' : item.answerVariance > 0 ? 'text-warning' : 'text-success'">
                                {{ item.answerVariance?.toFixed(1) || '0.0' }}%
                            </span>
                        </template>
                        <template #item.guidelinePass="{ item }">
                            <span :class="getGuidelineColor(item.guidelinePassCount, item.guidelineTotalCount)">
                                {{ item.guidelinePassCount || 0 }}/{{ item.guidelineTotalCount || 0 }}
                            </span>
                        </template>
                        <template #item.guidelineVariance="{ item }">
                            <span :class="item.guidelineVariance > 10 ? 'text-error' : item.guidelineVariance > 0 ? 'text-warning' : 'text-success'">
                                {{ item.guidelineVariance?.toFixed(1) || '0.0' }}%
                            </span>
                        </template>
                        <template #item.exactPass="{ item }">
                            <span :class="getGuidelineColor(item.exactPassCount, item.exactTotalCount)">
                                {{ item.exactPassCount || 0 }}/{{ item.exactTotalCount || 0 }}
                            </span>
                        </template>
                        <template #item.exactVariance="{ item }">
                            <span :class="item.exactVariance > 10 ? 'text-error' : item.exactVariance > 0 ? 'text-warning' : 'text-success'">
                                {{ item.exactVariance?.toFixed(1) || '0.0' }}%
                            </span>
                        </template>
                    </v-data-table>
                    <div class="px-4 py-2 bg-grey-lighten-5 text-caption">
                        <strong>Remarks:</strong>
                        <ul class="mt-1 mb-0" style="padding-left: 16px;">
                            <li><strong>Acc%:</strong> Overall answer accuracy (pass/total API calls)</li>
                            <li><strong>Val%:</strong> Combined answer variance % (lower is better)</li>
                            <li><strong>Guideline:</strong> pass/total - per-call evaluations for guideline type</li>
                            <li><strong>gVal%:</strong> Guideline answer variance % (lower is better)</li>
                            <li><strong>Exact:</strong> pass/total - per-call evaluations for exact type</li>
                            <li><strong>eVal%:</strong> Exact answer variance % (lower is better)</li>
                        </ul>
                    </div>
                </v-card>

                <!-- Detailed Question Results - Focus on Answer Evaluation -->
                <v-card variant="outlined">
                    <v-card-title class="text-subtitle-1 pa-3 bg-grey-lighten-4 d-flex align-center">
                        <v-icon class="mr-2" size="small">mdi-format-list-checks</v-icon>
                        Detailed Answer Evaluation Results
                        <v-spacer></v-spacer>
                        <v-text-field v-model="questionSearchQuery" placeholder="Search questions..."
                            prepend-inner-icon="mdi-magnify" variant="outlined" density="compact" hide-details
                            style="max-width: 250px" clearable></v-text-field>
                    </v-card-title>

                    <!-- Filter Chips -->
                    <div class="px-3 py-2 bg-grey-lighten-5 d-flex ga-2 flex-wrap align-center">
                        <span class="text-caption text-grey mr-2">Filter:</span>
                        <v-chip v-for="filter in questionFilters" :key="filter.value"
                            :color="selectedQuestionFilter === filter.value ? 'primary' : 'default'"
                            :variant="selectedQuestionFilter === filter.value ? 'flat' : 'outlined'" size="small"
                            @click="selectedQuestionFilter = filter.value">
                            {{ filter.text }} ({{ filter.count }})
                        </v-chip>
                    </div>

                    <!-- Question Cards -->
                    <div class="question-cards-container pa-3">
                        <div v-if="paginatedQuestions.length === 0" class="text-center py-8 text-grey">
                            <v-icon size="48" color="grey-lighten-1">mdi-file-search-outline</v-icon>
                            <div class="mt-2">No questions match your filter</div>
                        </div>

                        <v-card v-for="item in paginatedQuestions" :key="item._uniqueId" class="question-card mb-3"
                            variant="outlined">
                            <!-- Question Card Header (Clickable to expand/collapse) -->
                            <div class="question-card-header pa-3 d-flex align-center justify-space-between cursor-pointer"
                                :class="getAnswerPassClass(item)"
                                @click="toggleQuestionExpand(item._uniqueId)">
                                <div class="d-flex align-center flex-grow-1" style="min-width: 0;">
                                    <v-chip size="small" :color="getAnswerPassColor(item)" variant="flat"
                                        class="mr-2 flex-shrink-0">
                                        #{{ item.displayNo }}
                                    </v-chip>
                                    <span class="text-body-1 font-weight-medium text-truncate">{{ item.question
                                        }}</span>
                                    <v-chip size="x-small" variant="tonal" class="ml-2 flex-shrink-0">
                                        {{ "Category: " + item.category }}
                                    </v-chip>
                                </div>
                                <div class="d-flex align-center flex-shrink-0 ml-2">
                                    <v-chip size="small" :color="getAnswerPassColor(item)" variant="flat"
                                        class="font-weight-bold">
                                        {{ getAnswerPassLabel(item) }}
                                    </v-chip>
                                    <v-icon class="ml-1" size="small">
                                        {{ isQuestionExpanded(item._uniqueId) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                    </v-icon>
                                </div>
                            </div>

                            <!-- Collapsible Content -->
                            <v-expand-transition>
                                <v-card-text v-show="isQuestionExpanded(item._uniqueId)" class="pa-0">
                                    <!-- Section 1: EXPECTED -->
                                    <div class="pa-3 bg-grey-lighten-5 border-b">
                                        <div class="text-subtitle-2 mb-2 d-flex align-center">
                                            <v-icon size="small" class="mr-2" color="primary">mdi-target</v-icon>
                                            <span class="text-primary font-weight-bold">EXPECTED</span>
                                        </div>

                                        <!-- Expected Intent Path - Show when hasIntentValidation -->
                                        <div v-if="hasIntentValidation" class="mb-2">
                                            <span class="text-caption-new font-weight-bolder text-grey-darken-2 font-size-larger">Intent: </span>
                                            <span class="text-body-2">
                                                {{ formatExpectedIntentPath(item.expected) }}
                                            </span>
                                        </div>

                                        <!-- Expected Answer -->
                                        <div>
                                            <span class="text-caption-new font-weight-medium text-grey-darken-2">
                                                Answer ({{ item.answerType || 'guideline' }}):
                                            </span>
                                            <div v-if="item.expectedAnswer"
                                                class="text-body-2 mt-1 pa-2 bg-white rounded expandable-text"
                                                :class="{ 'cursor-pointer': shouldShowSeeMore(item.expectedAnswer) }"
                                                style="border: 1px solid #e0e0e0;"
                                                @click="shouldShowSeeMore(item.expectedAnswer) && toggleTextExpand(item._uniqueId, 'expectedAnswer')">
                                                <template v-if="isTextExpanded(item._uniqueId, 'expectedAnswer')">
                                                    {{ item.expectedAnswer }}
                                                    <span class="text-caption text-primary ml-1 see-more-link">(Show
                                                        less)</span>
                                                </template>
                                                <template v-else>
                                                    {{ truncateToLines(item.expectedAnswer).text }}
                                                    <v-tooltip v-if="shouldShowSeeMore(item.expectedAnswer)"
                                                        location="top">
                                                        <template #activator="{ props }">
                                                            <span v-bind="props"
                                                                class="text-caption text-primary ml-1 see-more-link">See
                                                                more</span>
                                                        </template>
                                                        <span>Click to see full text</span>
                                                    </v-tooltip>
                                                </template>
                                            </div>
                                            <div v-else
                                                class="text-caption text-grey font-italic mt-1 pa-2 bg-orange-lighten-5 rounded d-flex align-center"
                                                style="border: 1px solid #ffe0b2;">
                                                <v-icon size="small" class="mr-1"
                                                    color="orange">mdi-alert-circle-outline</v-icon>
                                                Answer guideline not provided - answer evaluation will be skipped
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Section 2: TEST RESULTS (Calls) -->
                                    <div class="pa-3">
                                        <div class="text-subtitle-2 mb-3 d-flex align-center">
                                            <v-icon size="small" class="mr-2" color="info">mdi-flask-outline</v-icon>
                                            <span class="text-info font-weight-bold">TEST RESULTS</span>
                                            <v-chip size="x-small" color="info" variant="tonal" class="ml-2">
                                                {{ item.reliabilityCalls?.length || 0 }} call{{
                                                    (item.reliabilityCalls?.length || 0)
                                                !== 1 ? 's' : '' }}
                                            </v-chip>
                                        </div>

                                        <!-- Each Call Result -->
                                        <div v-for="(call, callIdx) in (item.reliabilityCalls || [])" :key="callIdx"
                                            class="call-result-box mb-3 pa-3 rounded"
                                            :class="getCallResultClass(call)">
                                            <div class="text-caption font-weight-bold mb-2 text-grey-darken-2">
                                                Call {{ callIdx + 1 }}
                                            </div>

                                            <!-- Intent Row - Show when hasIntentValidation -->
                                            <div v-if="hasIntentValidation" class="d-flex align-start mb-3">
                                                <span class="text-caption font-weight-medium text-grey-darken-2"
                                                    style="min-width: 120px;">Intent:</span>
                                                <div class="flex-grow-1">
                                                    <span v-if="isValidIntent(call.intent)" class="text-body-2">
                                                        {{ call.intent }}
                                                        <v-icon size="small" class="ml-1"
                                                            :color="call.match ? 'success' : 'error'">
                                                            {{ call.match ? 'mdi-check-circle' : 'mdi-close-circle' }}
                                                        </v-icon>
                                                        <v-chip size="x-small" color="info" variant="tonal" class="ml-1">
                                                            {{ formatConfidence(call.confidence) }}%
                                                        </v-chip>
                                                    </span>
                                                    <span v-else class="text-grey text-body-2 font-italic">
                                                        (no intent detected)
                                                        <v-icon size="small" class="ml-1" color="error">mdi-close-circle</v-icon>
                                                    </span>
                                                </div>
                                            </div>

                                            <!-- Answer Row -->
                                            <div class="d-flex align-start mb-3">
                                                <!-- v-if="call.botAnswer" -->
                                                <span class="text-caption font-weight-medium text-grey-darken-2"
                                                    style="min-width: 120px;">Answer:</span>
                                                <div class="text-body-2 flex-grow-1 expandable-text"
                                                    :class="{ 'cursor-pointer': shouldShowSeeMore(call.botAnswer) }"
                                                    @click="shouldShowSeeMore(call.botAnswer) && toggleTextExpand(item._uniqueId, 'botAnswer_' + callIdx)">
                                                    <template
                                                        v-if="isTextExpanded(item._uniqueId, 'botAnswer_' + callIdx)">
                                                        {{ call.botAnswer }}
                                                        <span class="text-caption text-primary ml-1 see-more-link">(Show
                                                            less)</span>
                                                    </template>
                                                    <template v-else>
                                                        {{ truncateToLines(call.botAnswer).text }}
                                                        <v-tooltip v-if="shouldShowSeeMore(call.botAnswer)"
                                                            location="top">
                                                            <template #activator="{ props }">
                                                                <span v-bind="props"
                                                                    class="text-caption text-primary ml-1 see-more-link">See
                                                                    more</span>
                                                            </template>
                                                            <span>Click to see full text</span>
                                                        </v-tooltip>
                                                    </template>
                                                </div>
                                            </div>

                                            <!-- Answer Evaluation Row -->
                                            <div class="d-flex align-start">
                                                <span class="text-caption font-weight-medium text-grey-darken-2"
                                                    style="min-width: 120px;">Answer Eval:</span>
                                                <div class="flex-grow-1">
                                                    <!-- Has evaluation result -->
                                                    <div v-if="call.hasAnswerEval" class="d-flex align-top">
                                                        <v-icon size="small" class="mr-1"
                                                            :color="call.answerEvalPass ? 'success' : 'error'">
                                                            {{ call.answerEvalPass ? 'mdi-check-circle' :
                                                            'mdi-close-circle' }}
                                                        </v-icon>
                                                        <span class="font-weight-bold text-body-2"
                                                            :class="call.answerEvalPass ? 'text-success' : 'text-error'">
                                                            {{ call.answerEvalPass ? 'PASS' : 'FAIL' }}
                                                        </span>
                                                        <span v-if="call.answerEvalReason"
                                                            class="text-body-2 text-grey-darken-1 ml-1">
                                                            - {{ call.answerEvalReason }}
                                                        </span>
                                                    </div>
                                                    <!-- No guideline provided -->
                                                    <div v-else-if="!item.expectedAnswer"
                                                        class="text-caption text-orange font-italic d-flex align-center">
                                                        <v-icon size="x-small" class="mr-1"
                                                            color="orange">mdi-minus-circle-outline</v-icon>
                                                        Skipped - No guideline provided
                                                    </div>
                                                    <!-- Guideline exists but no eval data -->
                                                    <div v-else
                                                        class="text-caption text-grey font-italic d-flex align-center">
                                                        <v-icon size="x-small" class="mr-1"
                                                            color="grey">mdi-minus-circle-outline</v-icon>
                                                        Not evaluated
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Consistency Indicator -->
                                        <v-chip size="small" :color="item.reliabilityConsistent || item.answerConsistent ? 'success' : 'warning'"
                                            :variant="item.reliabilityConsistent || item.answerConsistent ? 'flat' : 'flat'" class="mt-1">
                                            <v-icon size="small" class="mr-1">
                                                {{ (item.reliabilityConsistent || item.answerConsistent) ? 'mdi-check-circle' : 'mdi-alert' }}
                                            </v-icon>
                                            <template v-if="item.reliabilityConsistent || item.answerConsistent">
                                                <span v-if="hasIntentValidation && hasAnswerValidation">Consistent (Intent & Answer)</span>
                                                <span v-else-if="hasIntentValidation">Consistent Intent</span>
                                                <span v-else>Consistent Answer</span>
                                            </template>
                                            <template v-else-if="item.varianceType === 'both'">
                                                Variance in Intent & Answer
                                            </template>
                                            <template v-else-if="item.varianceType === 'intent'">
                                                Variance in Intent
                                            </template>
                                            <template v-else-if="item.varianceType === 'answer'">
                                                Variance in Answer
                                            </template>
                                            <template v-else>
                                                Variance Detected
                                            </template>
                                        </v-chip>
                                    </div>
                                </v-card-text>
                            </v-expand-transition>
                        </v-card>

                        <!-- Pagination -->
                        <div v-if="totalQuestionPages > 1" class="d-flex justify-center mt-4">
                            <v-pagination v-model="currentQuestionPage" :length="totalQuestionPages" :total-visible="7"
                                density="compact"></v-pagination>
                        </div>
                    </div>
                </v-card>
            </div>

            <!-- No Data State -->
            <div v-else class="text-center py-8">
                <v-icon size="64" color="grey-lighten-1">mdi-clipboard-text-off-outline</v-icon>
                <div class="mt-4 text-h6 text-grey">No job data found</div>
                <v-btn color="primary" variant="outlined" class="mt-4" @click="goBack">
                    <v-icon class="mr-1">mdi-arrow-left</v-icon>
                    Back to Dashboard
                </v-btn>
            </div>

            <!-- Export Dialog -->
            <v-dialog v-model="exportDialog" max-width="400">
                <v-card>
                    <v-card-title class="text-h6 pa-4">Export Answer Quality Report</v-card-title>
                    <v-card-text>
                        <v-radio-group v-model="exportFormat">
                            <v-radio label="Answer Quality Report (TXT)" value="txt"></v-radio>
                            <v-radio label="Detailed Answer Comparison (TXT)" value="detailed_txt"></v-radio>
                            <v-radio label="Summary JSON" value="json"></v-radio>
                        </v-radio-group>
                    </v-card-text>
                    <v-card-actions class="pa-4">
                        <v-spacer></v-spacer>
                        <v-btn variant="outlined" @click="exportDialog = false">Cancel</v-btn>
                        <v-btn color="primary" variant="elevated" @click="doExport">
                            <v-icon class="mr-1">mdi-download</v-icon>
                            Download
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-container>
    </div>
</template>

<script>
export default {
    name: 'ValidationJobDetail',
    inject: ["flagNav", "FontColorTitle", "SubNavbarBackGround"],
    data() {
        return {
            loading: false,
            today: '',

            // Job ID from route
            rootJobId: null,

            // Core data
            selectedJob: null,
            selectedRunId: null,
            selectedRunData: null,
            detailResults: [],
            runHistoryData: [],

            // UI state
            questionSearchQuery: '',
            selectedQuestionFilter: 'all',
            currentQuestionPage: 1,
            questionsPerPage: 20,
            exportDialog: false,
            exportFormat: 'txt',
            expandedQuestions: {},
            expandedTexts: {},

            // Table headers - Answer Quality Summary ONLY
            answerQualityCategoryHeaders: [
                { title: 'Category', key: 'category', width: '200px' },
                { title: 'N', key: 'n', width: '50px', align: 'center' },
                { title: 'R', key: 'reliabilityCount', width: '50px', align: 'center' },
                { title: 'API', key: 'apiCalls', width: '60px', align: 'center' },
                { title: 'Acc%', key: 'answerAccuracy', width: '80px', align: 'center' },
                { title: 'Val%', key: 'answerVariance', width: '80px', align: 'center' },
                { title: 'Guideline', key: 'guidelinePass', width: '100px', align: 'center' },
                { title: 'gVal%', key: 'guidelineVariance', width: '80px', align: 'center' },
                { title: 'Exact', key: 'exactPass', width: '100px', align: 'center' },
                { title: 'eVal%', key: 'exactVariance', width: '80px', align: 'center' },
            ],
            // Table headers - Intent Summary
            intentCategoryHeaders: [
                { title: 'Category', key: 'category', width: '200px' },
                { title: 'N', key: 'n', width: '50px', align: 'center' },
                { title: 'R', key: 'reliabilityCount', width: '50px', align: 'center' },
                { title: 'API', key: 'apiCalls', width: '60px', align: 'center' },
                { title: 'Acc%', key: 'accuracy', width: '80px', align: 'center' },
                { title: 'Acc(L4+)%', key: 'accuracyL4', width: '90px', align: 'center' },
                { title: 'Acc(L3+)%', key: 'accuracyL3', width: '90px', align: 'center' },
                { title: 'Acc(L2+)%', key: 'accuracyL2', width: '90px', align: 'center' },
                { title: 'TTFC(ms)', key: 'ttfc', width: '90px', align: 'center' },
                { title: 'Total(ms)', key: 'totalTime', width: '90px', align: 'center' },
                { title: 'Fallback%', key: 'fallback', width: '90px', align: 'center' },
                { title: 'cVal%', key: 'cVal', width: '70px', align: 'center' },
                { title: 'sVal%', key: 'sVal', width: '70px', align: 'center' },
            ],
        };
    },

    computed: {
        // Check if this job has intent validation
        hasIntentValidation() {
            const vt = this.selectedRunData?.validationType;
            return vt === 'intent' || vt === 'both';
        },

        // Check if this job has answer validation
        hasAnswerValidation() {
            const vt = this.selectedRunData?.validationType;
            return vt === 'answer' || vt === 'both';
        },

        // Guideline Pass Rate
        guidelinePassRate() {
            if (!this.selectedRunData || this.selectedRunData.guidelineTotalCount === 0) return 0;
            return ((this.selectedRunData.guidelinePassCount / this.selectedRunData.guidelineTotalCount) * 100).toFixed(1);
        },

        // Exact Pass Rate
        exactPassRate() {
            if (!this.selectedRunData || this.selectedRunData.exactTotalCount === 0) return 0;
            return ((this.selectedRunData.exactPassCount / this.selectedRunData.exactTotalCount) * 100).toFixed(1);
        },

        // Intent Category Breakdown with Average Row (for intent/both validation types)
        intentCategoryBreakdownWithAverage() {
            const categories = this.selectedRunData?.categoryBreakdown || [];
            if (categories.length === 0) return [];

            // Calculate averages
            const totalN = categories.reduce((sum, cat) => sum + (cat.n || 0), 0);
            const totalApiCalls = categories.reduce((sum, cat) => sum + (cat.apiCalls || 0), 0);
            const avgReliability = categories.length > 0 ? categories[0].reliabilityCount : 1;
            
            const avgAccuracy = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.accuracy || cat.intentAccuracy || 0), 0) / categories.length 
                : 0;
            const avgAccuracyL4 = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.accuracyL4 || 0), 0) / categories.length 
                : 0;
            const avgAccuracyL3 = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.accuracyL3 || 0), 0) / categories.length 
                : 0;
            const avgAccuracyL2 = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.accuracyL2 || 0), 0) / categories.length 
                : 0;
            
            const catsWithTtfc = categories.filter(cat => cat.ttfc != null);
            const avgTtfc = catsWithTtfc.length > 0 
                ? catsWithTtfc.reduce((sum, cat) => sum + cat.ttfc, 0) / catsWithTtfc.length 
                : null;
            
            const catsWithTotalTime = categories.filter(cat => cat.totalTime != null);
            const avgTotalTime = catsWithTotalTime.length > 0 
                ? catsWithTotalTime.reduce((sum, cat) => sum + cat.totalTime, 0) / catsWithTotalTime.length 
                : null;
            
            const avgFallback = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.fallback || cat.fallbackRate || 0), 0) / categories.length 
                : 0;
            const avgCVal = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.cVal || 0), 0) / categories.length 
                : 0;
            const avgSVal = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.sVal || 0), 0) / categories.length 
                : 0;

            // Map categories for display
            const mappedCategories = categories.map(cat => ({
                ...cat,
                accuracy: cat.accuracy || cat.intentAccuracy || 0,
                fallback: cat.fallback || cat.fallbackRate || 0,
            }));

            const averageRow = {
                category: 'AVERAGE',
                n: totalN,
                reliabilityCount: avgReliability,
                apiCalls: totalApiCalls,
                accuracy: avgAccuracy,
                accuracyL4: avgAccuracyL4,
                accuracyL3: avgAccuracyL3,
                accuracyL2: avgAccuracyL2,
                ttfc: avgTtfc,
                totalTime: avgTotalTime,
                fallback: avgFallback,
                cVal: avgCVal,
                sVal: avgSVal,
                isAverage: true,
            };

            return [...mappedCategories, averageRow];
        },

        // Answer Quality Category Breakdown with Average Row
        answerQualityCategoryBreakdownWithAverage() {
            const categories = this.selectedRunData?.categoryBreakdown || [];
            if (categories.length === 0) return [];

            // Map categories with calculated answer quality metrics
            const mappedCategories = categories.map(cat => {
                const answerTotal = cat.answerTotalCount || 0;
                const answerPass = cat.answerPassCount || 0;
                const answerAccuracy = answerTotal > 0 ? (answerPass / answerTotal) * 100 : 0;
                
                return {
                    ...cat,
                    answerAccuracy: answerAccuracy,
                    answerVariance: 0,
                    guidelineVariance: 0,
                    exactVariance: 0,
                };
            });

            // Calculate averages
            const totalN = mappedCategories.reduce((sum, cat) => sum + (cat.n || 0), 0);
            const totalApiCalls = mappedCategories.reduce((sum, cat) => sum + (cat.apiCalls || 0), 0);
            const avgReliability = mappedCategories.length > 0 ? mappedCategories[0].reliabilityCount : 1;
            
            const totalAnswerPass = mappedCategories.reduce((sum, cat) => sum + (cat.answerPassCount || 0), 0);
            const totalAnswerTotal = mappedCategories.reduce((sum, cat) => sum + (cat.answerTotalCount || 0), 0);
            const avgAnswerAccuracy = totalAnswerTotal > 0 ? (totalAnswerPass / totalAnswerTotal) * 100 : 0;
            
            const totalGuidelinePass = mappedCategories.reduce((sum, cat) => sum + (cat.guidelinePassCount || 0), 0);
            const totalGuidelineTotal = mappedCategories.reduce((sum, cat) => sum + (cat.guidelineTotalCount || 0), 0);
            
            const totalExactPass = mappedCategories.reduce((sum, cat) => sum + (cat.exactPassCount || 0), 0);
            const totalExactTotal = mappedCategories.reduce((sum, cat) => sum + (cat.exactTotalCount || 0), 0);
            
            const avgAnswerVariance = mappedCategories.length > 0 
                ? mappedCategories.reduce((sum, cat) => sum + (cat.answerVariance || 0), 0) / mappedCategories.length 
                : 0;
            const avgGuidelineVariance = mappedCategories.length > 0 
                ? mappedCategories.reduce((sum, cat) => sum + (cat.guidelineVariance || 0), 0) / mappedCategories.length 
                : 0;
            const avgExactVariance = mappedCategories.length > 0 
                ? mappedCategories.reduce((sum, cat) => sum + (cat.exactVariance || 0), 0) / mappedCategories.length 
                : 0;

            const averageRow = {
                category: 'AVERAGE',
                n: totalN,
                reliabilityCount: avgReliability,
                apiCalls: totalApiCalls,
                answerAccuracy: avgAnswerAccuracy,
                answerVariance: avgAnswerVariance,
                guidelinePassCount: totalGuidelinePass,
                guidelineTotalCount: totalGuidelineTotal,
                guidelineVariance: avgGuidelineVariance,
                exactPassCount: totalExactPass,
                exactTotalCount: totalExactTotal,
                exactVariance: avgExactVariance,
                isAverage: true,
            };

            return [...mappedCategories, averageRow];
        },

        // Runs for selected job (for dropdown)
        selectedJobRuns() {
            if (!this.selectedJob) return [];

            const runsData = this.runHistoryData.length > 0 ? this.runHistoryData : (this.selectedJob.runs || []);

            return runsData.map(run => ({
                text: `Run #${run.runNumber || 1} - ${this.formatDateShort(run.completedAt)}`,
                value: run.id,
                jobId: run.jobId,
                answerQuality: run.answerQuality,
                avgTTFC: run.avgTTFC || run.averageTTFC,
                ttfcDisplay: this.formatTTFC(run.avgTTFC || run.averageTTFC),
                completedAt: run.completedAt,
            }));
        },

        // Question filters - Focus on Answer Pass/Fail
        questionFilters() {
            const results = this.detailResults;
            return [
                { text: 'All', value: 'all', count: results.length },
                { text: 'Answer Pass', value: 'pass', count: results.filter(r => this.hasAnswerPass(r)).length },
                { text: 'Answer Fail', value: 'fail', count: results.filter(r => this.hasAnswerFail(r)).length },
                { text: 'Variance', value: 'variance', count: results.filter(r => !r.answerConsistent).length },
            ];
        },

        filteredQuestions() {
            let results = [...this.detailResults];

            if (this.selectedQuestionFilter === 'pass') {
                results = results.filter(r => this.hasAnswerPass(r));
            } else if (this.selectedQuestionFilter === 'fail') {
                results = results.filter(r => this.hasAnswerFail(r));
            } else if (this.selectedQuestionFilter === 'variance') {
                results = results.filter(r => !r.answerConsistent);
            }

            if (this.questionSearchQuery) {
                const query = this.questionSearchQuery.toLowerCase();
                results = results.filter(r =>
                    r.question?.toLowerCase().includes(query) ||
                    r.category?.toLowerCase().includes(query)
                );
            }

            return results.map((item, index) => ({
                ...item,
                displayNo: index + 1,
                _uniqueId: `${item.masterId}-${index}`,
            }));
        },

        totalQuestionPages() {
            return Math.ceil(this.filteredQuestions.length / this.questionsPerPage);
        },

        paginatedQuestions() {
            const start = (this.currentQuestionPage - 1) * this.questionsPerPage;
            return this.filteredQuestions.slice(start, start + this.questionsPerPage);
        },
    },

    watch: {
        selectedRunId(newVal) {
            if (newVal) {
                this.loadRunData(newVal);
            }
        },

        selectedQuestionFilter() {
            this.currentQuestionPage = 1;
        },

        questionSearchQuery() {
            this.currentQuestionPage = 1;
        },
    },

    mounted() {
        this.GetDate();
        // Get job ID from route query parameter
        this.rootJobId = this.$route.query.id;
        if (this.rootJobId) {
            this.loadJobInfo();
        } else {
            console.error('No job ID provided in route');
        }
    },

    methods: {
        GetDate() {
            let today1 = new Date();
            let dd = today1.getDate();
            let mm = today1.getMonth() + 1;
            let yyyy = today1.getFullYear();
            if (dd < 10) dd = "0" + dd;
            if (mm < 10) mm = "0" + mm;
            this.today = dd + "/" + mm + "/" + yyyy;
        },

        // ==================== NAVIGATION ====================

        goBack() {
            this.$router.push({ name: 'ValidationDashboardV2' });
        },

        // ==================== ANSWER PASS/FAIL HELPERS ====================

        hasAnswerPass(item) {
            const calls = item.reliabilityCalls || [];
            return calls.some(c => c.hasAnswerEval && c.answerEvalPass === true);
        },

        hasAnswerFail(item) {
            const calls = item.reliabilityCalls || [];
            return calls.some(c => c.hasAnswerEval && c.answerEvalPass === false);
        },

        getAnswerPassColor(item) {
            const calls = item.reliabilityCalls || [];
            const hasEval = calls.some(c => c.hasAnswerEval);
            if (!hasEval) return 'grey';
            
            const allPass = calls.filter(c => c.hasAnswerEval).every(c => c.answerEvalPass);
            const allFail = calls.filter(c => c.hasAnswerEval).every(c => !c.answerEvalPass);
            
            if (allPass) return 'success';
            if (allFail) return 'error';
            return 'warning'; // Mixed results
        },

        getAnswerPassLabel(item) {
            const calls = item.reliabilityCalls || [];
            const hasEval = calls.some(c => c.hasAnswerEval);
            if (!hasEval) return 'N/A';
            
            const passCount = calls.filter(c => c.hasAnswerEval && c.answerEvalPass).length;
            const evalCount = calls.filter(c => c.hasAnswerEval).length;
            
            if (passCount === evalCount) return 'PASS';
            if (passCount === 0) return 'FAIL';
            return `${passCount}/${evalCount} PASS`;
        },

        getAnswerPassClass(item) {
            const color = this.getAnswerPassColor(item);
            if (color === 'success') return 'bg-green-lighten-5';
            if (color === 'error') return 'bg-red-lighten-5';
            if (color === 'warning') return 'bg-orange-lighten-5';
            return 'bg-grey-lighten-5';
        },

        // ==================== API CALLS ====================

        loadJobInfo() {
            this.loading = true;
            this.loadRunHistory(this.rootJobId);
        },

        loadRunHistory(rootJobId) {
            // V2 API: Use GetValidationJobRunHistory (not GetIntentTestJobRunHistory)
            this.$call.GetValidationJobRunHistory(
                rootJobId,
                (response) => {
                    console.log('=== GetValidationJobRunHistory Response ===', response);
                    if (response.status === 0 && response.data) {
                        this.runHistoryData = (response.data || []).map(run => {
                            console.log('Run data:', run);
                            return {
                                id: run.id,
                                jobId: run.jobId || run.jobID,
                                jobName: run.jobName,
                                runNumber: run.runNumber || 1,
                                validationType: run.validationType,  // Capture validationType
                                answerQuality: run.answerQuality || run.answerAccuracy || 0,
                                avgTTFC: run.avgTTFC || run.averageTTFC,
                                completedAt: run.completedAt,
                                linkedJobId: run.linkedJobId,
                                modelId: run.modelId,
                                modelName: run.modelName,
                                judgeModelId: run.judgeModelId,
                            };
                        }).sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

                        if (this.runHistoryData.length > 0) {
                            const firstRun = this.runHistoryData[0];
                            const baseName = (firstRun.jobName || 'Unnamed Job').replace(/ \(Rerun(?: #\d+)?\)$/, '');
                            this.selectedJob = {
                                rootJobId: rootJobId,
                                jobName: baseName,
                                validationType: firstRun.validationType,  // Store validationType
                                runs: this.runHistoryData,
                            };
                            console.log('Selected Job validationType:', firstRun.validationType);

                            this.selectedRunId = this.runHistoryData[0].id;
                        } else {
                            this.loading = false;
                        }
                    } else {
                        this.loading = false;
                    }
                },
                (error) => {
                    console.error('Failed to load run history:', error);
                    this.loading = false;
                }
            );
        },

        loadRunData(runId) {
            let run = this.runHistoryData.find(r => r.id === runId);
            if (!run) {
                run = this.selectedJob?.runs?.find(r => r.id === runId);
            }
            if (!run) return;

            this.selectedRunData = null;
            this.loading = true;

            // V2 API: Use GetValidationJobSummary (not GetIntentTestJobSummary)
            this.$call.GetValidationJobSummary(
                run.jobId,
                (response) => {
                    console.log('=== GetValidationJobSummary Response ===', response);
                    if (response.status === 0 && response.data) {
                        const data = response.data;
                        console.log('Summary data.validationType:', data.validationType);
                        this.selectedRunData = {
                            // Validation type (fallback to run's validationType if not in summary)
                            validationType: data.validationType || run.validationType || 'answer',
                            
                            // Intent fields (for intent/both validation types)
                            intentAccuracy: data.intentAccuracy,
                            l1Accuracy: data.l1Accuracy,
                            l2Accuracy: data.l2Accuracy,
                            l3Accuracy: data.l3Accuracy,
                            l4Accuracy: data.l4Accuracy,
                            correctCount: data.correctCount || 0,
                            fallbackRate: data.fallbackRate || 0,
                            fallbackCount: data.fallbackCount || 0,
                            
                            // Answer quality fields
                            answerQuality: data.answerQuality || 0,
                            answerPassCount: data.answerPassCount || 0,
                            answerEvalCount: data.answerEvalCount || 0,
                            totalQuestions: data.totalQuestions || 0,
                            totalApiCalls: data.totalApiCalls || 0,
                            avgTTFC: data.avgTTFC || null,
                            avgResponseTime: data.avgResponseTime || null,
                            ttfcMin: data.ttfcMin || null,
                            ttfcMax: data.ttfcMax || null,
                            ttfcMedian: data.ttfcMedian || null,
                            guidelinePassCount: data.guidelinePassCount || 0,
                            guidelineTotalCount: data.guidelineTotalCount || 0,
                            exactPassCount: data.exactPassCount || 0,
                            exactTotalCount: data.exactTotalCount || 0,
                            modelId: run.modelId || null,
                            judgeModelId: run.judgeModelId || null,
                            modelName: data.modelName || run.modelName || '-',
                            reliabilityCount: data.reliability || 1,
                            categoryBreakdown: (data.categoryBreakdown || []).map(cat => ({
                                category: cat.category || 'Uncategorized',
                                n: cat.n || cat.total || 0,
                                reliabilityCount: cat.reliability || data.reliability || 1,
                                apiCalls: cat.apiCalls || (cat.n || cat.total || 0) * (cat.reliability || data.reliability || 1),
                                // Intent fields
                                intentMatchCount: cat.intentMatchCount || 0,
                                intentAccuracy: cat.intentAccuracy,
                                // Answer quality fields
                                answerPassCount: cat.answerPassCount || 0,
                                answerTotalCount: cat.answerTotalCount || 0,
                                guidelinePassCount: cat.guidelinePassCount || 0,
                                guidelineTotalCount: cat.guidelineTotalCount || 0,
                                exactPassCount: cat.exactPassCount || 0,
                                exactTotalCount: cat.exactTotalCount || 0,
                                // Performance fields - map API field names to frontend field names
                                ttfc: cat.avgTTFC || cat.avgTtfc || null,
                                totalTime: cat.avgResponseTime || null,
                                fallback: cat.fallbackRate || 0,
                            })),
                        };

                        this.loadDetailResults(run.jobId);
                    }
                    this.loading = false;
                },
                (error) => {
                    console.error('Failed to load run data:', error);
                    this.loading = false;
                }
            );
        },

        loadDetailResults(jobId) {
            // V2 API: Use GetTableValidationResults (not GetTableIntentTestResult)
            this.$call.GetTableValidationResults(
                { JobId: jobId, PerPage: 5000 },
                (response) => {
                    if (response.status === 0 && response.data) {
                        const items = response.data.data || response.data.items || response.data || [];
                        this.detailResults = this.groupResultsByQuestion(items);
                    }
                },
                (error) => {
                    console.error('Failed to load detail results:', error);
                }
            );
        },

        groupResultsByQuestion(flatResults) {
            const grouped = {};

            flatResults.forEach(result => {
                // FIX: Use questionId (V2 API) instead of masterId (V1 API)
                const key = result.questionId || result.masterId;
                if (!grouped[key]) {
                    grouped[key] = {
                        masterId: result.questionId || result.masterId,
                        sessionId: result.sessionId,
                        question: result.question,
                        category: result.category,
                        reliabilityCalls: [],
                        answerEvals: [],
                        answerType: result.answerType || 'guideline',
                        // FIX: Use expectedAnswer (V2 API) instead of answerGuide (V1 API)
                        expectedAnswer: result.expectedAnswer || result.answerGuide || '',
                        latestBotAnswer: null,
                        // ADD: Expected intent object for formatExpectedIntentPath()
                        expected: {
                            L1: result.expectedIntentL1,
                            L2: result.expectedIntentL2,
                            L3: result.expectedIntentL3,
                            L4: result.expectedIntentL4,
                        },
                    };
                }

                grouped[key].reliabilityCalls.push({
                    // FIX: Use callNumber (V2 API) instead of reliabilityCallNumber (V1 API)
                    callNumber: result.callNumber || result.reliabilityCallNumber || 1,
                    // FIX: Use actualAnswer (V2 API) instead of botAnswer (V1 API)
                    botAnswer: result.actualAnswer || result.botAnswer || '',
                    hasAnswerEval: result.answerEvalPass !== null && result.answerEvalPass !== undefined,
                    answerEvalPass: result.answerEvalPass,
                    answerEvalReason: result.answerEvalReason || '',
                    // ADD: Intent fields for template display
                    intent: result.actualIntentFull || '',
                    match: result.isFullMatch,
                    confidence: result.confidenceL4 || result.confidenceL3 || result.confidenceL2 || result.confidenceL1,
                });

                // FIX: Use actualAnswer (V2 API) instead of botAnswer (V1 API)
                if (result.actualAnswer || result.botAnswer) {
                    grouped[key].latestBotAnswer = result.actualAnswer || result.botAnswer;
                }

                if (result.answerEvalPass !== null && result.answerEvalPass !== undefined) {
                    grouped[key].answerEvals.push({
                        pass: result.answerEvalPass,
                        reason: result.answerEvalReason || '',
                        // FIX: Use actualAnswer (V2 API) instead of botAnswer (V1 API)
                        botAnswer: result.actualAnswer || result.botAnswer || '',
                    });
                }
            });

            return Object.values(grouped).map((item, index) => {
                const calls = item.reliabilityCalls;
                
                // Check answer evaluation consistency
                const callsWithEval = calls.filter(c => c.hasAnswerEval);
                let answerConsistent = true;
                if (callsWithEval.length > 1) {
                    const firstEvalResult = callsWithEval[0].answerEvalPass;
                    answerConsistent = callsWithEval.every(c => c.answerEvalPass === firstEvalResult);
                }
                
                return {
                    ...item,
                    no: index + 1,
                    answerConsistent: answerConsistent,
                };
            });
        },

        // ==================== EXPORT FUNCTIONS ====================

        doExport() {
            this.exportDialog = false;
            if (!this.selectedRunData || !this.selectedJob) return;

            if (this.exportFormat === 'txt') {
                this.exportAnswerQualityTxt();
            } else if (this.exportFormat === 'detailed_txt') {
                this.exportDetailedAnswerTxt();
            } else if (this.exportFormat === 'json') {
                this.exportJson();
            }
        },

        exportAnswerQualityTxt() {
            const data = this.selectedRunData;
            const job = this.selectedJob;
            const lines = [];

            const separator80 = '='.repeat(80);
            const dash80 = '-'.repeat(80);

            lines.push(separator80);
            lines.push('ANSWER QUALITY REPORT');
            lines.push(separator80);
            lines.push('');
            lines.push('Job Name: ' + job.jobName);
            lines.push('Model: ' + data.modelName);
            lines.push('Total Questions: ' + data.totalQuestions);
            lines.push('');
            lines.push(dash80);
            lines.push('ANSWER QUALITY METRICS');
            lines.push(dash80);
            lines.push('Overall Answer Quality: ' + data.answerQuality + '%');
            lines.push('Guideline Pass Rate: ' + this.guidelinePassRate + '% (' + data.guidelinePassCount + '/' + data.guidelineTotalCount + ')');
            lines.push('Exact Match Rate: ' + this.exactPassRate + '% (' + data.exactPassCount + '/' + data.exactTotalCount + ')');
            lines.push('');
            lines.push(dash80);
            lines.push('RESPONSE TIME');
            lines.push(dash80);
            lines.push('Avg TTFC: ' + this.formatMs(data.avgTTFC));
            lines.push('Min TTFC: ' + this.formatMs(data.ttfcMin));
            lines.push('Max TTFC: ' + this.formatMs(data.ttfcMax));
            lines.push('Median TTFC: ' + this.formatMs(data.ttfcMedian));
            lines.push('');

            const content = lines.join('\n');
            this.downloadFile(content, `answer_quality_${job.jobName}_${Date.now()}.txt`, 'text/plain');
        },

        exportDetailedAnswerTxt() {
            const data = this.selectedRunData;
            const job = this.selectedJob;
            const lines = [];

            lines.push('DETAILED ANSWER EVALUATION REPORT');
            lines.push('='.repeat(80));
            lines.push('Job: ' + job.jobName);
            lines.push('');

            this.detailResults.forEach((item, idx) => {
                lines.push('-'.repeat(80));
                lines.push(`Q${idx + 1}: ${item.question}`);
                lines.push(`Category: ${item.category}`);
                lines.push(`Expected (${item.answerType}): ${item.expectedAnswer || 'N/A'}`);
                lines.push('');
                
                item.reliabilityCalls.forEach((call, callIdx) => {
                    lines.push(`  Call ${callIdx + 1}:`);
                    lines.push(`    Bot Answer: ${call.botAnswer || 'N/A'}`);
                    if (call.hasAnswerEval) {
                        lines.push(`    Evaluation: ${call.answerEvalPass ? 'PASS' : 'FAIL'} - ${call.answerEvalReason || ''}`);
                    } else {
                        lines.push(`    Evaluation: Not evaluated`);
                    }
                });
                lines.push('');
            });

            const content = lines.join('\n');
            this.downloadFile(content, `answer_detailed_${job.jobName}_${Date.now()}.txt`, 'text/plain');
        },

        exportJson() {
            const data = {
                jobName: this.selectedJob?.jobName,
                answerQuality: this.selectedRunData?.answerQuality,
                guidelinePassRate: this.guidelinePassRate,
                exactPassRate: this.exactPassRate,
                guidelinePass: this.selectedRunData?.guidelinePassCount,
                guidelineTotal: this.selectedRunData?.guidelineTotalCount,
                exactPass: this.selectedRunData?.exactPassCount,
                exactTotal: this.selectedRunData?.exactTotalCount,
                categoryBreakdown: this.selectedRunData?.categoryBreakdown,
            };

            const content = JSON.stringify(data, null, 2);
            this.downloadFile(content, `answer_quality_${this.selectedJob?.jobName}_${Date.now()}.json`, 'application/json');
        },

        downloadFile(content, filename, mimeType) {
            const blob = new Blob([content], { type: mimeType });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        },

        // ==================== FORMATTING ====================

        formatTTFC(ms) {
            if (!ms) return '-';
            return `${(ms / 1000).toFixed(1)}s`;
        },

        formatMs(ms) {
            if (!ms) return '-';
            if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
            return `${ms}ms`;
        },

        formatDateShort(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        },

        // Text expand/collapse methods
        toggleQuestionExpand(uniqueId) {
            this.expandedQuestions[uniqueId] = !this.expandedQuestions[uniqueId];
        },

        isQuestionExpanded(uniqueId) {
            return this.expandedQuestions[uniqueId] !== false;
        },

        getTextKey(uniqueId, fieldName) {
            return `${uniqueId}_${fieldName}`;
        },

        toggleTextExpand(uniqueId, fieldName) {
            const key = this.getTextKey(uniqueId, fieldName);
            this.expandedTexts[key] = !this.expandedTexts[key];
        },

        isTextExpanded(uniqueId, fieldName) {
            const key = this.getTextKey(uniqueId, fieldName);
            return this.expandedTexts[key] === true;
        },

        truncateToLines(text, maxChars = 250) {
            if (!text || text.length <= maxChars) return { text: text, isTruncated: false };
            return { text: text.substring(0, maxChars).trim() + '...', isTruncated: true };
        },

        shouldShowSeeMore(text, maxChars = 250) {
            return text && text.length > maxChars;
        },

        // ==================== STYLING HELPERS ====================

        getAnswerQualityClass(quality) {
            if (quality >= 90) return 'border-success';
            if (quality >= 70) return 'border-warning';
            return 'border-error';
        },

        getAccuracyClass(accuracy) {
            if (accuracy >= 90) return 'border-success';
            if (accuracy >= 70) return 'border-warning';
            return 'border-error';
        },

        getFallbackClass(rate) {
            if (rate <= 5) return 'border-success';
            if (rate <= 15) return 'border-warning';
            return 'border-error';
        },

        getScoreColor(score) {
            if (score >= 90) return 'success';
            if (score >= 70) return 'warning';
            return 'error';
        },

        getScoreTextColor(score) {
            if (score >= 90) return 'text-success';
            if (score >= 70) return 'text-warning';
            return 'text-error';
        },

        getGuidelineColor(pass, total) {
            if (total === 0) return 'text-grey';
            const rate = (pass / total) * 100;
            if (rate >= 90) return 'text-success';
            if (rate >= 70) return 'text-warning';
            return 'text-error';
        },

        // Intent-related helper methods
        formatExpectedIntentPath(expected) {
            if (!expected) return '-';
            const levels = [expected.L1, expected.L2, expected.L3, expected.L4].filter(l => l && l.trim());
            return levels.length > 0 ? levels.join(' > ') : '-';
        },

        formatConfidence(confidence) {
            if (!confidence && confidence !== 0) return '-';
            const pct = confidence > 1 ? confidence : confidence * 100;
            return pct.toFixed(1);
        },

        isValidIntent(intent) {
            if (!intent) return false;
            const cleaned = intent.replace(/[\s>-]+/g, '').trim();
            return cleaned.length > 0;
        },

        getCallResultClass(call) {
            // For jobs with both intent and answer, show green if both pass
            if (this.hasIntentValidation && this.hasAnswerValidation) {
                return (call.match && call.answerEvalPass) ? 'bg-green-lighten-5' : 'bg-red-lighten-5';
            }
            // For intent-only jobs
            if (this.hasIntentValidation) {
                return call.match ? 'bg-green-lighten-5' : 'bg-red-lighten-5';
            }
            // For answer-only jobs
            return call.answerEvalPass ? 'bg-green-lighten-5' : 'bg-red-lighten-5';
        },
    },
};
</script>

<style scoped>
.text-caption-new {
    font-size: 14px !important;
    font-weight: 500;
    line-height: 1.667;
    letter-spacing: 0.0333333333em !important;
    font-family: "Roboto", sans-serif;
    text-transform: none !important;
}

.summary-card {
    border-width: 2px !important;
    transition: transform 0.2s, box-shadow 0.2s;
    min-height: 164px !important;
}

.summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.border-success {
    border-color: rgb(var(--v-theme-success)) !important;
}

.border-warning {
    border-color: rgb(var(--v-theme-warning)) !important;
}

.border-error {
    border-color: rgb(var(--v-theme-error)) !important;
}

.category-table :deep(th) {
    background-color: #f5f5f5 !important;
    font-weight: 600 !important;
}

.question-cards-container {
    max-height: 800px;
    overflow-y: auto;
}

.question-card {
    transition: box-shadow 0.2s;
    overflow: hidden;
}

.question-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.question-card-header {
    background-color: #fafafa;
}

.call-result-box {
    border: 1px solid #e0e0e0;
}

.call-result-box.bg-green-lighten-5 {
    border-color: #c8e6c9;
}

.call-result-box.bg-red-lighten-5 {
    border-color: #ffcdd2;
}

.border-b {
    border-bottom: 1px solid #e0e0e0;
}

.cursor-pointer {
    cursor: pointer;
}

.expandable-text {
    line-height: 1.5;
}

.see-more-link {
    cursor: pointer;
    text-decoration: underline;
    font-weight: 500;
}

.see-more-link:hover {
    text-decoration: none;
}

.h-100 {
    height: 100%;
}
</style>
